/** @jsxImportSource @emotion/react */

import { FC } from 'react'
import { useMutation } from '@apollo/client'
import { createSearchParams } from 'react-router-dom'
import { FieldArray } from 'react-final-form-arrays'
import { CREATE_SUCCESS_STORY_SECTION } from 'queries/successStory'
import {
  createSuccessStorySection,
  createSuccessStorySectionVariables,
} from '__generatedTypes__/createSuccessStorySection'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { QUERY_PARAM, SUCCESS_STORIES_TAB } from 'pagesNew/BlogPages'
import { InputField } from 'components/Input'
import { validateRequired, validateRequiredField } from 'utils/validation'
import { searchByStoryteller_searchByStoryteller_items as Storyteller } from '__generatedTypes__/searchByStoryteller'
import { toast } from 'utils/toast'
import { useBroadcastChannel } from 'hooks/useBroadcastChannel'
import { REFETCH_SUCCESS_STORIES_MESSAGE } from 'pagesNew/SuccessStory/Create'
import { BROADCAST_CHANNEL } from 'constants/consts'
import { Form, FormValues } from './Form'
import { SuccessStoriesFieldArray } from './Form/SuccessStoriesFieldArray'
import * as classes from './Form/styles'

type Props = {
  platform: Platform
}

export const OrganizeCreateSuccessStory: FC = withPlatformCheck(({ platform }: Props) => {
  const { navigate } = useLink()

  const [createSuccessStorySection] = useMutation<createSuccessStorySection, createSuccessStorySectionVariables>(
    CREATE_SUCCESS_STORY_SECTION
  )

  const handleSubmit = async (values: FormValues) => {
    try {
      const { title, description = '' } = values

      await createSuccessStorySection({
        variables: {
          createSuccessStorySectionInput: {
            title,
            description,
            platform: {
              id: platform.id,
            },
            successStory: values.articles.map(({ id }) => ({ id })),
            successStoryIds: values.articles.map(({ id }) => id),
          },
        },
      })

      navigate(
        cmsRoutes.BLOG_PAGES,
        {},
        { searchParams: createSearchParams({ [QUERY_PARAM]: SUCCESS_STORIES_TAB.id }) }
      )
      toast.success('Success story section created successfully')
    } catch (error) {
      toast.error('Failed to create success story section')
    }
  }

  useBroadcastChannel(BROADCAST_CHANNEL, (event) => {
    if (event.data === REFETCH_SUCCESS_STORIES_MESSAGE) {
      toast.success('Success story created successfully')
    }
  })

  return (
    <Form
      platform={platform}
      onSubmit={handleSubmit}
      title="Organize Success Stories"
      createLink={cmsRoutes.CREATE_SUCCESS_STORY}
      renderFields={({ form, linkParams }) => (
        <>
          <InputField
            css={classes.field}
            name="description"
            validate={validateRequired}
            label="Description"
            placeholder="Enter Description"
            fullWidth
          />
          <FieldArray<Storyteller> name="articles" validate={validateRequiredField}>
            {({ fields, meta }) => (
              <SuccessStoriesFieldArray
                platform={platform}
                onChange={form.change}
                fields={fields}
                meta={meta}
                linkParams={linkParams}
              />
            )}
          </FieldArray>
        </>
      )}
    />
  )
})
